import { formatWhen } from "@/components/boo/access-copy";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  api,
  type ConnectorCheck,
  type ConnectorDetail,
  type ConnectorSaveResult,
  type ConnectorVariable,
  type ConnectorVerifyResult,
} from "@/lib/api";
import { invalidateConnectors } from "@/lib/connectors";
import { cn } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Link2, Loader2, PlugZap, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ChecksList, SecretBadge } from "./parts";

type Outcome = { saved: boolean; result: ConnectorVerifyResult };

// What the form needs; the setup page gets only this from the backend.
export type ConnectionView = Pick<ConnectorDetail, "slug" | "name" | "variables" | "connection"> & { live: boolean };

export function ConnectionPanel({ connector, containerId }: { connector: ConnectorDetail; containerId: string }) {
  const queryClient = useQueryClient();
  const { canConfigure, canConnect } = connector._permissions;
  return (
    <ConnectionForm
      connector={{ ...connector, live: connector.running !== null }}
      containerId={containerId}
      canEdit={canConnect ?? canConfigure}
      canManage={canConfigure}
      save={(values) => api.connectors.saveVariables(containerId, connector.slug, values)}
      onChanged={() => invalidateConnectors(queryClient, containerId)}
    />
  );
}

// The values the connector needs. Saving runs its verify with them: every
// check must pass, or nothing is saved. Secret values are never shown again
// and can only be replaced; only the ones a person typed are sent.
// canManage adds Test connection and Disconnect; setup (the form-only page)
// also drops Copy link.
export function ConnectionForm({
  connector,
  containerId,
  canEdit,
  canManage,
  save: saveValues,
  onChanged: invalidate,
  onSaved,
  onSaveError,
  setup = false,
  hideStatus = false,
}: {
  connector: ConnectionView;
  containerId: string;
  canEdit: boolean;
  canManage: boolean;
  save: (values: Record<string, string>) => Promise<ConnectorSaveResult>;
  onChanged: () => void;
  // Called after a save that passed its connection test.
  onSaved?: (result: ConnectorVerifyResult) => void;
  // Shows a failed save itself instead of the error toast; true when it did.
  onSaveError?: (err: unknown) => boolean;
  setup?: boolean;
  // The one-time link page has no stored connection to describe.
  hideStatus?: boolean;
}) {
  const live = connector.live;
  const initial = () =>
    Object.fromEntries(connector.variables.filter((v) => !v.secret).map((v) => [v.name, v.value ?? ""]));
  const [values, setValues] = useState<Record<string, string>>(initial);
  // Secrets being replaced (or set for the first time): name → typed value.
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const changed: Record<string, string> = {};
  for (const v of connector.variables) {
    if (v.secret) {
      if (secrets[v.name] !== undefined && secrets[v.name] !== "") changed[v.name] = secrets[v.name];
    } else if ((values[v.name] ?? "") !== (v.value ?? "")) {
      changed[v.name] = values[v.name] ?? "";
    }
  }
  const missing = connector.variables.filter((v) =>
    v.secret ? !v.set && !changed[v.name] : !(values[v.name] ?? "").trim() && !keepsWhenEmpty(v),
  );
  const dirty = Object.keys(changed).length > 0;

  const localErrors = (): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const v of connector.variables) {
      const value = v.secret ? changed[v.name] : values[v.name];
      if (value === undefined || value === "") continue;
      if (v.pattern) {
        try {
          if (!new RegExp(`^(?:${v.pattern})$`).test(value)) out[v.name] = "That doesn’t look right for this field.";
        } catch {
          // A pattern the browser can't parse is left to the backend.
        }
      }
    }
    return out;
  };

  const save = useMutation({
    mutationFn: () => saveValues(changed),
    onSuccess: (res) => {
      if (res.saved) {
        setOutcome({ saved: true, result: res });
        setSecrets({});
        setErrors({});
        toast.success(res.account ? `Connected to ${res.account}` : "Connected");
        invalidate();
        onSaved?.(res);
      } else if (res.code === "verify_failed") {
        setOutcome({ saved: false, result: res });
        toast.error("The connection test failed, so nothing was saved");
      } else {
        setErrors(res.errors);
        setOutcome(null);
      }
    },
    onError: (err) => {
      if (!onSaveError?.(err)) toast.error(err instanceof Error ? err.message : "Could not save");
    },
  });

  const test = useMutation({
    mutationFn: () => api.connectors.verify(containerId, connector.slug),
    onSuccess: (res) => {
      const ok = res.checks.every((c) => c.ok);
      setOutcome({ saved: true, result: res });
      if (ok) toast.success("Connection works");
      else toast.error("The connection test failed");
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not test the connection"),
  });

  const disconnect = useMutation({
    mutationFn: () => api.connectors.disconnect(containerId, connector.slug),
    onSuccess: (res) => {
      setConfirmDisconnect(false);
      setValues(Object.fromEntries(connector.variables.filter((v) => !v.secret).map((v) => [v.name, ""])));
      setSecrets({});
      setOutcome(null);
      toast.success("Disconnected", { description: res.rotateNotice, duration: 10_000 });
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not disconnect"),
  });

  const submit = () => {
    const found = localErrors();
    setErrors(found);
    if (Object.keys(found).length === 0) save.mutate();
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/containers/${encodeURIComponent(containerId)}/integrations/custom/${encodeURIComponent(connector.slug)}?tab=connection`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: "Send it privately to whoever holds the key." });
    } catch {
      toast.info(url);
    }
  };

  const connected = connector.connection?.ok === true && connector.variables.every((v) => v.set);
  const shownChecks: { checks: ConnectorCheck[]; title: string; failed: boolean } | null = outcome
    ? {
        checks: outcome.result.checks,
        failed: !outcome.saved || !outcome.result.checks.every((c) => c.ok),
        title: outcome.saved ? "Connection test" : "Connection test failed. Nothing was saved.",
      }
    : connector.connection?.checks?.length
      ? { checks: connector.connection.checks, failed: connector.connection.ok === false, title: "Last test" }
      : null;

  if (!live) {
    return (
      <div className="flex flex-col gap-4">
        <p className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          {setup
            ? `${connector.name} isn’t running yet. Once it is, fill in what it needs here.`
            : "Nothing runs yet. Once a version is running, fill in what it needs here, and saving runs its connection test."}
        </p>
        <VariableSummary variables={connector.variables} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {!hideStatus && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border p-4">
          <div className="min-w-0 flex-1">
            {connected ? (
              <>
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  <Check className="size-4 text-emerald-600" />
                  {connector.connection?.account ? `Connected to ${connector.connection.account}` : "Connected"}
                </p>
                {connector.connection?.checkedAt && (
                  <p className="text-xs text-muted-foreground">Last tested {formatWhen(connector.connection.checkedAt)}</p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm font-medium">Not connected</p>
                <p className="text-xs text-muted-foreground">Boo can’t use {connector.name} until these are filled in.</p>
              </>
            )}
          </div>
          {!setup && (
            <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => void copyLink()}>
              <Link2 className="size-3.5" />
              Copy link
            </Button>
          )}
          {canManage && connector.variables.every((v) => v.set) && (
            <Button size="sm" variant="outline" className="gap-1.5" disabled={test.isPending} onClick={() => test.mutate()}>
              {test.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <PlugZap className="size-3.5" />}
              Test connection
            </Button>
          )}
        </div>
      )}

      <form
        className="ph-no-capture flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {connector.variables.map((v) => (
          <div key={v.name} className="flex flex-col gap-1.5">
            <Label
              htmlFor={v.secret && v.set && secrets[v.name] === undefined ? undefined : `var-${v.name}`}
              className="flex items-center gap-2"
            >
              {v.label}
              {v.secret && <SecretBadge className="h-5 font-normal" />}
            </Label>
            {v.secret ? (
              <SecretField
                variable={v}
                error={errors[v.name]}
                value={secrets[v.name]}
                disabled={!canEdit}
                onChange={(next) =>
                  setSecrets((s) => {
                    const copy = { ...s };
                    if (next === undefined) delete copy[v.name];
                    else copy[v.name] = next;
                    return copy;
                  })
                }
              />
            ) : (
              <Input
                id={`var-${v.name}`}
                value={values[v.name] ?? ""}
                disabled={!canEdit}
                autoComplete="off"
                aria-invalid={!!errors[v.name]}
                aria-describedby={errors[v.name] ? `var-${v.name}-error` : undefined}
                placeholder={keepsWhenEmpty(v) ? "Leave empty to keep the current value" : undefined}
                onChange={(e) => setValues((s) => ({ ...s, [v.name]: e.target.value }))}
              />
            )}
            {errors[v.name] && (
              <p id={`var-${v.name}-error`} className="text-xs text-destructive">
                {errors[v.name]}
              </p>
            )}
          </div>
        ))}

        {canEdit && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground text-pretty">
              Saving runs the connection test first. If any check fails, nothing is saved.
            </p>
            <Button type="submit" size="sm" disabled={!dirty || missing.length > 0 || save.isPending}>
              {save.isPending ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  Testing…
                </>
              ) : (
                "Save and test"
              )}
            </Button>
          </div>
        )}
      </form>

      {shownChecks && (
        <section className="flex flex-col gap-2">
          <h2 className={cn("text-sm font-semibold", shownChecks.failed && "text-destructive")}>{shownChecks.title}</h2>
          {outcome?.saved && outcome.result.account && !shownChecks.failed && (
            <p className="text-sm text-muted-foreground">Connected to {outcome.result.account}</p>
          )}
          <ChecksList checks={shownChecks.checks} />
        </section>
      )}

      {canManage && connector.variables.some((v) => v.set) && (
        <div className="flex items-center justify-between gap-3 rounded-lg border p-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">Disconnect</p>
            <p className="text-xs text-muted-foreground">Deletes every value stored for this connection.</p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="gap-1.5 text-destructive hover:text-destructive"
            onClick={() => setConfirmDisconnect(true)}
          >
            <Trash2 className="size-4" />
            Disconnect
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmDisconnect}
        onOpenChange={setConfirmDisconnect}
        title={`Disconnect ${connector.name}?`}
        description="Every stored value is deleted and Boo can’t use it until someone connects it again. The key still works at the service itself, so revoke or rotate it there too."
        confirmLabel="Disconnect"
        loading={disconnect.isPending}
        onConfirm={() => disconnect.mutate()}
      />
    </div>
  );
}

// The one-time link page isn't sent stored values, not even non-secret ones:
// a field that's set but has no value shown keeps it when left empty.
const keepsWhenEmpty = (v: ConnectorVariable) => !v.secret && v.set && v.value == null;

function SecretField({
  variable,
  error,
  value,
  disabled,
  onChange,
}: {
  variable: ConnectorVariable;
  error?: string;
  value: string | undefined;
  disabled: boolean;
  onChange: (next: string | undefined) => void;
}) {
  const replacing = value !== undefined || !variable.set;
  if (!replacing) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex h-9 flex-1 items-center rounded-md border bg-muted/40 px-3 font-mono text-sm tracking-widest text-muted-foreground select-none">
          ••••••••••••
        </div>
        {!disabled && (
          <Button type="button" size="sm" variant="outline" aria-label={`Replace ${variable.label}`} onClick={() => onChange("")}>
            Replace
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Input
        id={`var-${variable.name}`}
        aria-invalid={!!error}
        aria-describedby={error ? `var-${variable.name}-error` : undefined}
        type="password"
        value={value ?? ""}
        disabled={disabled}
        autoComplete="new-password"
        placeholder={variable.set ? "New value" : undefined}
        autoFocus={variable.set}
        onChange={(e) => onChange(e.target.value)}
      />
      {variable.set && (
        <Button type="button" size="sm" variant="ghost" onClick={() => onChange(undefined)}>
          Cancel
        </Button>
      )}
    </div>
  );
}

function VariableSummary({ variables }: { variables: ConnectorVariable[] }) {
  if (variables.length === 0) return null;
  return (
    <div className="divide-y rounded-lg border">
      {variables.map((v) => (
        <div key={v.name} className="flex items-center gap-3 p-3">
          <p className="min-w-0 flex-1 text-sm">{v.label}</p>
          {v.secret && <SecretBadge />}
        </div>
      ))}
    </div>
  );
}
